import { useEffect, useRef } from "react";

const NEXT_PAGE_DISTANCE_PX = 180;

/** Share pane/document scrolling while keeping the first page bounded.
 * Mount, resize and layout changes never trigger speculative extra pages.
 * Scrolling loads nothing until the visitor clicks "Load more" once, so the
 * footer stays reachable; after that, nearing the control loads the next page.
 */
export function useCatalogueScroll(enabled: boolean, loadMore: () => void) {
  const button = useRef<HTMLButtonElement>(null);
  const current = useRef({ enabled, loadMore });
  current.current = { enabled, loadMore };
  useEffect(() => {
    const positions = new WeakMap<EventTarget, number>();
    let armed = false;
    const onClick = (event: Event) => {
      const control = button.current;
      if (control && event.target instanceof Node && control.contains(event.target)) armed = true;
    };
    const onScroll = (event: Event) => {
      const target = event.target;
      const control = button.current;
      if (!control || !target || control.getClientRects().length === 0) return;
      const isDocument = target === document;
      if (!isDocument && (!(target instanceof HTMLElement) || !target.contains(control))) return;
      const scroller = isDocument ? document.scrollingElement : target as HTMLElement;
      const top = scroller?.scrollTop ?? window.scrollY;
      const previous = positions.get(target) ?? 0;
      positions.set(target, top);
      if (!armed || top <= previous || !current.current.enabled) return;
      const viewportBottom = isDocument ? window.innerHeight : (target as HTMLElement).getBoundingClientRect().bottom;
      if (control.getBoundingClientRect().top <= viewportBottom + NEXT_PAGE_DISTANCE_PX) {
        current.current.loadMore();
      }
    };
    // Scroll does not bubble; capture sees both nested panes and the document.
    document.addEventListener("scroll", onScroll, true);
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("scroll", onScroll, true);
      document.removeEventListener("click", onClick, true);
    };
  }, []);
  return button;
}
